import { getElement } from '@stencil/core';
import { debounce } from '../throttle';
import * as getset from './get-set';

export type StorageParams = Record<string, any>;

export type StorageMethods =
  | 'url-hash'
  | 'url-query'
  | 'url-hash-push'
  | 'url-query-push'
  | 'local'
  | 'session';

interface Component {
  connectedCallback?(): void;
  disconnectedCallback?(): void;
  componentDidRender?(): void;
  [key: string]: any;
}

const store = new Map<string, Store>();

export const ComponentStore = {
  init(
    component: Component,
    keys: string[],
    method?: StorageMethods,
    id?: string
  ) {
    const getId = id || component.storeId || getElement(component).id;
    if (!getId) {
      console.error(
        'No id set. You can only instantiate a new component store with an id. '
      );
      return;
    }
    if (store.get(getId)) {
      console.error(
        'Duplicate store id usage. A store ID must be unique!',
        getId
      );
      return;
    }

    const initialState = keys.reduce((acc, key) => {
      if (typeof component[key] !== undefined) acc[key] = component[key];
      return acc;
    }, {});

    store.set(getId, new Store(getId, component, keys, initialState, method));
    const componentstore = store.get(getId);
    componentstore.dispatchToProps();

    const connectedCallback = component.connectedCallback;
    component.connectedCallback = function () {
      store.set(getId, new Store(getId, component, keys, initialState, method));
      const componentstore = store.get(getId);
      if (connectedCallback) {
        connectedCallback.call(component);
      }
      if (componentstore) componentstore.dispatchToProps();
    };

    const componentDidRender = component.componentDidRender;
    component.componentDidRender = function () {
      const componentstore = store.get(getId);
      if (componentDidRender) {
        componentDidRender.call(component);
      }
      if (componentstore) componentstore.savePropsToStore();
    };

    const disconnectedCallback = component.disconnectedCallback;
    component.disconnectedCallback = function () {
      const componentstore = store.get(getId);
      if (componentstore) componentstore.delete();
      store.delete(getId);
      if (disconnectedCallback) {
        disconnectedCallback.call(component);
      }
    };
  },
};

class Store {
  private method: StorageMethods = 'session';
  private keys: string[];
  private id: string;
  private component: Component;
  private initialState: { [key: string]: any } = {};
  private updateStore: boolean = true;

  constructor(
    id: string,
    component: Component,
    keys: string[],
    initialState?: any,
    method?: StorageMethods
  ) {
    if (!id)
      throw new Error(
        'No id set. You can only instantiate a new component store with an id'
      );
    this.id = id;
    this.keys = keys;
    this.component = component;
    if (initialState) this.initialState = initialState;
    if (method) this.method = method;

    this.savePropsToStore = debounce(this.savePropsToStore, 500);

    if (this.method === 'url-hash-push' || this.method === 'url-query-push') {
      window.addEventListener('popstate', this.onUrlChange);
    }
  }

  private onUrlChange = () => {
    this.dispatchToProps(false);
  };

  private equality(val1: any, val2: any) {
    if (typeof val1 !== typeof val2) return false;
    if (typeof val1 !== 'object') return val1 === val2;
    return JSON.stringify(val1) === JSON.stringify(val2);
  }

  get getData() {
    const data = this.keys.reduce((acc, key) => {
      const isEqual = this.equality(
        this.component[key],
        this.initialState[key]
      );
      if (typeof this.component[key] !== 'undefined' && !isEqual) {
        acc[key] = this.component[key];
      } else if (isEqual) delete acc[key];
      return acc;
    }, {});
    return Object.keys(data).length ? data : undefined;
  }

  private retrieve() {
    switch (this.method) {
      case 'url-hash':
      case 'url-hash-push':
        return getset.urlGet(this.id, 'hash');
      case 'url-query':
      case 'url-query-push':
        return getset.urlGet(this.id, 'query');
      case 'local':
        return getset.storageGet(this.id);
      default:
        return getset.sessionGet(this.id);
    }
  }

  private save(data: any) {
    switch (this.method) {
      case 'url-hash':
        return getset.urlSet(this.id, data, 'hash');
      case 'url-query':
        return getset.urlSet(this.id, data, 'query');
      case 'url-hash-push':
        return getset.urlSet(this.id, data, 'hash', window, 'push');
      case 'url-query-push':
        return getset.urlSet(this.id, data, 'query', window, 'push');
      case 'local':
        return getset.storageSet(this.id, data);
      default:
        return getset.sessionSet(this.id, data);
    }
  }

  public savePropsToStore = () => {
    if (this.updateStore) {
      this.save(this.getData);
    } else if (!this.updateStore) this.updateStore = true;
  };

  public dispatchToProps = (updateStore = true) => {
    const savedData = this.retrieve();
    if (!savedData && updateStore) return;

    // console.log('dispatching', savedData, this.id, this.initialState);
    this.updateStore = updateStore;

    this.keys.forEach((key) => {
      this.component[key] = savedData ? savedData[key] : undefined;
    });
  };

  public delete = () => {
    if (this.method === 'url-hash-push' || this.method === 'url-query-push') {
      window.removeEventListener('popstate', this.onUrlChange);
    }
  };
}
